import { useState } from 'react'

import type { PersonType } from '@/features/proposta/entities/Comprador/opcoes-comprador-menu'
import type { TProcurador } from '@/features/proposta/interfaces/compradores.types'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type {
  TClientePessoaFisicaPropostaSchema,
  TClientePessoaJuridicaPropostaSchema,
  TCompradorPropostaSchema,
} from '@/features/proposta/schemas'
import type { TEstadoCivilEnum } from '@/types/cliente'
import type { TClienteRegimeCasamentoEnum, TTipoAssinaturaEnum } from '@/types/proposta'
import type { TOrigemTelefone } from '@/utils/schemas/telefone.schema'

export type TClienteCompleto = {
  id: string
  tipo:
    | 'PessoaFisica'
    | 'PessoaJuridica'
    | 'MaePai'
    | 'Procurador'
    | 'Conjuge'
    | 'Representante'
    | 'ConjugeFluxoIndependente'
  nome: string
  documento: string
  estadoCivil?: TEstadoCivilEnum | null
  regimeCasamento?: TClienteRegimeCasamentoEnum | null
  uniaoEstavel?: 'sim' | 'nao' | null
  nacionalidadeId: string
  profissaoId: string
  maiorDe18Anos: 'sim' | 'nao' | null
  imagem?: string | null
  tipoAssinatura?: TTipoAssinaturaEnum | null
  telefone: {
    numero: string
    principal: boolean
    origem: TOrigemTelefone
  }
  email: string
  vinculo: Omit<TClienteCompleto, 'vinculo'> | null
}

export const useOrquestrarCriacaoEdicao = (
  compradores: TCompradorPropostaSchema[],
  clientes: TClientePessoaFisicaPropostaSchema[],
  clientesJuridicos: TClientePessoaJuridicaPropostaSchema[],
) => {
  const [flowKey, setFlowKey] = useState<PersonType | null>(null)
  const [clienteToEdit, setClienteToEdit] = useState<TClienteCompleto | null>(null)
  const mode: 'edit' | 'create' = clienteToEdit ? 'edit' : 'create'

  const openCreate = (tipoCliente: PersonType) => {
    setClienteToEdit(null)
    setFlowKey(tipoCliente)
  }

  const handleCloseStepsComprador = () => {
    setFlowKey(null)
    setClienteToEdit(null)
  }

  const handleEditRepresentante = (
    clienteParaEdicao: TCompradoresCadastrados[0]['vinculos'][0],
    compradorPJ: TCompradoresCadastrados[0],
  ): TClienteCompleto => {
    const clientePJCompleto = clientesJuridicos.find((c) => c.id === compradorPJ.id)!
    const clienteCompleteEdicao = clientes.find((c) => c.id === clienteParaEdicao.id)!
    return {
      id: clientePJCompleto.id,
      tipo: 'PessoaJuridica',
      nome: clientePJCompleto.razaoSocial,
      documento: clientePJCompleto.cnpj,
      nacionalidadeId: '',
      profissaoId: '',
      maiorDe18Anos: null,
      imagem: null,
      telefone: {
        numero: clientePJCompleto.telefone.numero,
        principal: clientePJCompleto.telefone.principal,
        origem: clientePJCompleto.telefone.origem,
      },
      email: clientePJCompleto.email,
      estadoCivil: null,
      regimeCasamento: null,
      uniaoEstavel: null,
      tipoAssinatura: null,
      vinculo: {
        id: clienteCompleteEdicao.id,
        tipo: 'Representante',
        nome: clienteCompleteEdicao.nome,
        documento: clienteCompleteEdicao.cpf,
        nacionalidadeId: clienteCompleteEdicao.nacionalidadeId,
        profissaoId: clienteCompleteEdicao.profissaoId,
        maiorDe18Anos: clienteCompleteEdicao.maiorDe18Anos ? 'sim' : 'nao',
        imagem: clienteCompleteEdicao.imagem,
        telefone: {
          numero: clienteCompleteEdicao.telefone.numero,
          principal: true,
          origem: clienteCompleteEdicao.telefone.origem,
        },
        email: clienteCompleteEdicao.email,
        estadoCivil: clienteCompleteEdicao.estadoCivil,
        regimeCasamento: clienteCompleteEdicao.regimeCasamento,
        uniaoEstavel: clienteCompleteEdicao.possuiUniaoEstavel ? 'sim' : 'nao',
      },
    }
  }

  const handleEditPJ = (clienteParaEdicao: TCompradoresCadastrados[0]): TClienteCompleto => {
    const clientePJCompleto = clientesJuridicos.find((c) => c.id === clienteParaEdicao.id)!

    return {
      id: clientePJCompleto.id,
      tipo: 'PessoaJuridica',
      nome: clientePJCompleto.razaoSocial,
      documento: clientePJCompleto.cnpj,
      nacionalidadeId: '',
      profissaoId: '',
      maiorDe18Anos: null,
      imagem: null,
      telefone: {
        numero: clientePJCompleto.telefone.numero,
        principal: clientePJCompleto.telefone.principal,
        origem: clientePJCompleto.telefone.origem,
      },
      email: clientePJCompleto.email,
      estadoCivil: null,
      regimeCasamento: null,
      uniaoEstavel: null,
      tipoAssinatura: null,
      vinculo: null,
    }
  }

  const handleEditCompradorPF = (clienteParaEdicao: TCompradoresCadastrados[0]): TClienteCompleto => {
    const clientePFCompleto = clientes.find((c) => c.id === clienteParaEdicao.id)!
    const conjugeId = clienteParaEdicao.vinculos.find((v) => v.tipo === 'Conjuge')?.id

    const conjugeCompleto = clientes.find((c) => c.id === conjugeId)
    const compradorTipoAssinatura = compradores.find((c) => c.clienteId === clientePFCompleto.id)?.tipoAssinatura

    return {
      id: clientePFCompleto.id,
      tipo: 'PessoaFisica',
      nome: clientePFCompleto.nome,
      documento: clientePFCompleto.cpf,
      nacionalidadeId: clientePFCompleto.nacionalidadeId,
      profissaoId: clientePFCompleto.profissaoId,
      maiorDe18Anos: clientePFCompleto.maiorDe18Anos ? 'sim' : 'nao',
      imagem: clientePFCompleto.imagem,
      telefone: {
        numero: clientePFCompleto.telefone.numero,
        principal: true,
        origem: clientePFCompleto.telefone.origem,
      },
      email: clientePFCompleto.email,
      estadoCivil: clientePFCompleto.estadoCivil,
      regimeCasamento: clientePFCompleto.regimeCasamento,
      uniaoEstavel: clientePFCompleto.possuiUniaoEstavel ? 'sim' : 'nao',
      tipoAssinatura: compradorTipoAssinatura,
      vinculo: conjugeCompleto
        ? {
            telefone: conjugeCompleto.telefone,
            imagem: conjugeCompleto.imagem,
            documento: conjugeCompleto.cpf,
            id: conjugeCompleto.id,
            tipo: 'Conjuge',
            nome: conjugeCompleto.nome,
            nacionalidadeId: conjugeCompleto.nacionalidadeId,
            profissaoId: conjugeCompleto.profissaoId,
            maiorDe18Anos: conjugeCompleto.maiorDe18Anos ? 'sim' : 'nao',
            email: conjugeCompleto.email,
            regimeCasamento: clientePFCompleto.regimeCasamento,
            estadoCivil: clientePFCompleto.estadoCivil,
            uniaoEstavel: clientePFCompleto.possuiUniaoEstavel ? 'sim' : 'nao',
          }
        : null,
    }
  }

  const handleEditConjuge = (
    clienteParaEdicao: TCompradoresCadastrados[0]['vinculos'][0],
    compradorPF: TCompradoresCadastrados[0],
  ): TClienteCompleto => {
    const conjugeCompleto = clientes.find((c) => c.id === clienteParaEdicao.id)!
    const clientePFCompleto = clientes.find((c) => c.id === compradorPF.id)!
    const compradorTipoAssinatura = compradores.find((c) => c.clienteId === compradorPF.id)?.tipoAssinatura
    return {
      id: clientePFCompleto.id,
      tipo: 'PessoaFisica',
      nome: clientePFCompleto.nome,
      documento: clientePFCompleto.cpf,
      nacionalidadeId: clientePFCompleto.nacionalidadeId,
      profissaoId: clientePFCompleto.profissaoId,
      maiorDe18Anos: clientePFCompleto.maiorDe18Anos ? 'sim' : 'nao',
      imagem: clientePFCompleto.imagem,
      telefone: {
        numero: clientePFCompleto.telefone.numero,
        principal: true,
        origem: clientePFCompleto.telefone.origem,
      },
      email: clientePFCompleto.email,
      estadoCivil: clientePFCompleto.estadoCivil,
      regimeCasamento: clientePFCompleto.regimeCasamento,
      uniaoEstavel: clientePFCompleto.possuiUniaoEstavel ? 'sim' : 'nao',
      tipoAssinatura: compradorTipoAssinatura,
      vinculo: {
        telefone: {
          numero: conjugeCompleto.telefone.numero,
          principal: true,
          origem: conjugeCompleto.telefone.origem,
        },
        documento: conjugeCompleto.cpf,
        id: conjugeCompleto.id,
        tipo: 'ConjugeFluxoIndependente',
        nome: conjugeCompleto.nome,
        nacionalidadeId: conjugeCompleto.nacionalidadeId,
        profissaoId: conjugeCompleto.profissaoId,
        maiorDe18Anos: conjugeCompleto.maiorDe18Anos ? 'sim' : 'nao',
        email: conjugeCompleto.email,
        regimeCasamento: clientePFCompleto.regimeCasamento,
        estadoCivil: clientePFCompleto.estadoCivil,
        uniaoEstavel: clientePFCompleto.possuiUniaoEstavel ? 'sim' : 'nao',
      },
    }
  }

  const handleEditMaePai = (
    clienteParaEdicao: TCompradoresCadastrados[0]['vinculos'][0],
    compradorPF: TCompradoresCadastrados[0],
  ): TClienteCompleto => {
    const maePaiCompleto = clientes.find((c) => c.id === clienteParaEdicao.id)!
    const uniaoEstavel = maePaiCompleto.possuiUniaoEstavel ? 'sim' : 'nao'
    const compradorCompleto = clientes.find((c) => c.id === compradorPF.id)!
    return {
      documento: compradorCompleto.cpf,
      email: compradorCompleto.email,
      id: compradorCompleto.id,
      imagem: compradorCompleto.imagem,
      maiorDe18Anos: 'nao',
      nacionalidadeId: compradorCompleto.nacionalidadeId,
      profissaoId: compradorCompleto.profissaoId,
      nome: compradorCompleto.nome,
      telefone: {
        numero: compradorCompleto.telefone.numero,
        origem: compradorCompleto.telefone.origem,
        principal: true,
      },
      tipo: 'PessoaFisica',
      estadoCivil: compradorCompleto.estadoCivil,
      regimeCasamento: compradorCompleto.regimeCasamento,
      uniaoEstavel: compradorCompleto.possuiUniaoEstavel ? 'sim' : 'nao',
      tipoAssinatura: null,
      vinculo: {
        id: maePaiCompleto.id,
        tipo: 'MaePai',
        nome: maePaiCompleto.nome,
        documento: maePaiCompleto.cpf,
        nacionalidadeId: maePaiCompleto.nacionalidadeId,
        profissaoId: maePaiCompleto.profissaoId,
        maiorDe18Anos: maePaiCompleto.maiorDe18Anos ? 'sim' : 'nao',
        imagem: maePaiCompleto.imagem,
        telefone: {
          numero: maePaiCompleto.telefone.numero,
          principal: true,
          origem: maePaiCompleto.telefone.origem,
        },
        email: maePaiCompleto.email,
        estadoCivil: maePaiCompleto.estadoCivil,
        regimeCasamento: maePaiCompleto.regimeCasamento,
        uniaoEstavel: maePaiCompleto.possuiUniaoEstavel === null ? null : uniaoEstavel,
        tipoAssinatura: null,
      },
    }
  }

  const handleEditProcurador = (clienteParaEdicao: TProcurador): TClienteCompleto => {
    const clienteCompleto = clientes.find((c) => c.id === clienteParaEdicao.id)!
    const uniaoEstavel = clienteCompleto.possuiUniaoEstavel ? 'sim' : 'nao'

    return {
      id: clienteCompleto.id,
      tipo: 'Procurador',
      nome: clienteCompleto.nome,
      documento: clienteCompleto.cpf,
      nacionalidadeId: clienteCompleto.nacionalidadeId,
      profissaoId: clienteCompleto.profissaoId,
      maiorDe18Anos: clienteCompleto.maiorDe18Anos ? 'sim' : 'nao',
      imagem: clienteCompleto.imagem,
      telefone: {
        numero: clienteCompleto.telefone.numero,
        principal: true,
        origem: clienteCompleto.telefone.origem,
      },
      email: clienteCompleto.email,
      estadoCivil: clienteCompleto.estadoCivil,
      regimeCasamento: clienteCompleto.regimeCasamento,
      uniaoEstavel: uniaoEstavel,
      tipoAssinatura: null,
      vinculo: null,
    }
  }

  const handleEdit = (
    cliente: TCompradoresCadastrados[0] | TCompradoresCadastrados[0]['vinculos'][0] | TProcurador,
    compradorCadastrado: TCompradoresCadastrados[0],
  ) => {
    switch (cliente.tipo) {
      case 'Procurador': {
        const clienteEdicao = handleEditProcurador(cliente)
        setClienteToEdit(clienteEdicao)
        setFlowKey('Procurador')
        break
      }
      case 'MaePai': {
        const clienteEdicao = handleEditMaePai(cliente, compradorCadastrado)
        setClienteToEdit(clienteEdicao)
        setFlowKey('MaePai')
        break
      }
      case 'Conjuge': {
        const clienteEdicao = handleEditConjuge(cliente, compradorCadastrado)
        setClienteToEdit(clienteEdicao)
        setFlowKey('PessoaFisica')
        break
      }
      case 'PessoaFisica': {
        const clienteEdicao = handleEditCompradorPF(cliente)
        setClienteToEdit(clienteEdicao)
        setFlowKey('PessoaFisica')
        break
      }
      case 'PessoaJuridica': {
        const clienteEdicao = handleEditPJ(cliente)
        setClienteToEdit(clienteEdicao)
        setFlowKey('PessoaJuridica')
        break
      }
      case 'Representante': {
        const clienteEdicao = handleEditRepresentante(cliente, compradorCadastrado)
        setClienteToEdit(clienteEdicao)
        setFlowKey('Representante')
        break
      }
      default:
        setFlowKey(null)
        return
    }
  }
  return {
    flowKey,
    mode,
    clienteToEdit,
    openCreate,
    handleCloseStepsComprador,
    handleEdit,
  }
}
